import React, { useState, useEffect } from 'react';
import { login, setToken, validateRegistration } from '../api.js';

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry"
];

const QUALIFICATIONS = [
  'MD (Pulmonary) First Year',
  'MD (Pulmonary) Second Year',
  'MD (Pulmonary) Third Year',
  'DNB (Pulmonary) First Year',
  'DNB (Pulmonary) Second Year',
  'DNB (Pulmonary) Third Year',
  'DM (Pulmonary) First Year',
  'DM (Pulmonary) Second Year',
  'DM (Pulmonary) Third Year'
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_PATTERN = /^[6-9]\d{9}$/;

const normalizeEmail = (email) => email.trim().toLowerCase();
const normalizePhone = (phone) => phone.replace(/\D/g, '').slice(0, 10);

function LoginForm({ onLogin }) {
  const [doctor1, setDoctor1] = useState({
    doctorName: '',
    qualification: '',
    phoneNumber: '',
    email: '',
    consent: false
  });

  const [doctor2, setDoctor2] = useState({
    doctorName: '',
    qualification: '',
    phoneNumber: '',
    email: '',
    consent: false
  });

  const [institute, setInstitute] = useState({
    collegeFullName: '',
    hodName: '',
    hodEmail: '',
    hodPhoneNumber: '',
    state: '',
    city: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function fieldValue(target) {
    if (target.type === 'checkbox') return target.checked;
    return target.name === 'phoneNumber' ? normalizePhone(target.value) : target.value;
  }

  function updateDoctor1(e) {
    const { name } = e.target;
    const value = fieldValue(e.target);
    setDoctor1(current => ({ ...current, [name]: value }));
  }

  function updateDoctor2(e) {
    const { name } = e.target;
    const value = fieldValue(e.target);
    setDoctor2(current => ({ ...current, [name]: value }));
  }

  function updateInstitute(e) {
    const { name, value } = e.target;
    setInstitute(current => ({
      ...current,
      [name]: name === 'hodPhoneNumber' ? normalizePhone(value) : value
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setError('');

    const doctor1Email = normalizeEmail(doctor1.email);
    const doctor2Email = normalizeEmail(doctor2.email);
    const hodEmail = normalizeEmail(institute.hodEmail);

    if (!EMAIL_PATTERN.test(doctor1Email) || !EMAIL_PATTERN.test(doctor2Email)) {
      setError('Enter a valid email address for both participants.');
      return;
    }

    if (doctor1Email === doctor2Email) {
      setError('Each participant must use a different email address.');
      return;
    }

    if (!PHONE_PATTERN.test(doctor1.phoneNumber) || !PHONE_PATTERN.test(doctor2.phoneNumber)) {
      setError('Enter a valid 10-digit Indian mobile number for both participants.');
      return;
    }

    if (doctor1.phoneNumber === doctor2.phoneNumber) {
      setError('Each participant must use a different phone number.');
      return;
    }

    if (!doctor1.consent || !doctor2.consent) {
      setError('Both participants must agree to the registration and communication consent.');
      return;
    }

    if (!EMAIL_PATTERN.test(hodEmail)) {
      setError('Enter a valid HOD email address.');
      return;
    }

    if (!PHONE_PATTERN.test(institute.hodPhoneNumber)) {
      setError('Enter a valid 10-digit HOD contact number.');
      return;
    }

    const formData = {
      collegeFullName: institute.collegeFullName,
      hodName: institute.hodName,
      hodEmail,
      hodPhoneNumber: institute.hodPhoneNumber,
      state: institute.state,
      city: institute.city,
      doctor1Name: doctor1.doctorName,
      doctor1Qualification: doctor1.qualification,
      doctor1PhoneNumber: doctor1.phoneNumber,
      doctor1Email,
      doctor2Name: doctor2.doctorName,
      doctor2Qualification: doctor2.qualification,
      doctor2PhoneNumber: doctor2.phoneNumber,
      doctor2Email,
      doctorName: `${doctor1.doctorName} & ${doctor2.doctorName}`,
      qualification: `${doctor1.qualification} / ${doctor2.qualification}`,
      phoneNumber: doctor1.phoneNumber,
      email: doctor1Email
    };

    setLoading(true);
    try {
      const validation = await validateRegistration(formData);
      if (!validation.valid) {
        setError(validation.message || 'Please correct the registration details.');
        return;
      }

      const data = await login(formData);
      
      if (data.hasCompletedQuiz && data.quizResult) {
        setError('A submission already exists for the provided email details.');
        return;
      }

      if (data.alreadyAttempted) {
        setError('A submission already exists for the provided email details.');
        return;
      }

      if (data.token) {
        setToken(data.token);
        await onLogin({
          token: data.token,
          hasCompletedQuiz: data.hasCompletedQuiz,
          quizResult: data.quizResult
        });
      } else if (data.message) {
        setError(data.message);
      } else {
        setError('Login failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-overlay">
      <div className="login-card">
        <form className="login-form" onSubmit={submit}>
          <p className="registration-team-note">Register as a team of two contestants.</p>

          <div className="doctor-section">
            <h3 className="section-title">Institute Details</h3>
            <p className="institute-note">Both participants must belong to this institute.</p>

            <div className="form-group">
              <label>College/Institute Full Name <span className="required">*</span></label>
              <input
                name="collegeFullName"
                value={institute.collegeFullName}
                onChange={updateInstitute}
                required
              />
            </div>

            <div className="form-group">
              <label>State <span className="required">*</span></label>
              <select name="state" value={institute.state} onChange={updateInstitute} required>
                <option value="">Select state</option>
                {INDIAN_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>City <span className="required">*</span></label>
              <input name="city" value={institute.city} onChange={updateInstitute} required />
            </div>

            <div className="form-group">
              <label>HOD Name <span className="required">*</span></label>
              <input
                name="hodName"
                value={institute.hodName}
                onChange={updateInstitute}
                required
              />
            </div>

            <div className="form-group">
              <label>HOD Email ID <span className="required">*</span></label>
              <input
                name="hodEmail"
                type="email"
                value={institute.hodEmail}
                onChange={updateInstitute}
                maxLength="254"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group">
              <label>HOD Contact No. <span className="required">*</span></label>
              <input
                name="hodPhoneNumber"
                type="tel"
                value={institute.hodPhoneNumber}
                onChange={updateInstitute}
                placeholder="10 digit number"
                maxLength="10"
                inputMode="numeric"
                pattern="[6-9][0-9]{9}"
                title="Enter a valid 10-digit Indian mobile number"
                autoComplete="tel"
                required
              />
            </div>

          </div>

          <div className="doctor-section">
            <h3 className="section-title">Participant 1 Details</h3>
            
            <div className="form-group">
              <label>Doctor Name <span className="required">*</span></label>
              <input 
                name="doctorName" 
                value={doctor1.doctorName} 
                onChange={updateDoctor1} 
                required 
              />
            </div>

            <div className="form-group">
              <label>Qualification <span className="required">*</span></label>
                <select
                  name="qualification"
                  value={doctor1.qualification}
                  onChange={updateDoctor1}
                  required
                >
                  <option value="">Select qualification</option>
                  {QUALIFICATIONS.map(qualification => (
                    <option key={qualification} value={qualification}>{qualification}</option>
                  ))}
                </select>
            </div>

            <div className="form-group">
              <label>WhatsApp Number <span className="required">*</span></label>
              <input 
                name="phoneNumber"
                type="tel"
                value={doctor1.phoneNumber} 
                onChange={updateDoctor1} 
                placeholder="10 digit number"
                maxLength="10"
                inputMode="numeric"
                pattern="[6-9][0-9]{9}"
                title="Enter a valid 10-digit Indian mobile number"
                autoComplete="tel"
                required
              />
            </div>

            <div className="form-group">
              <label>Email <span className="required">*</span></label>
              <input 
                name="email" 
                type="email" 
                value={doctor1.email}
                onChange={updateDoctor1}
                maxLength="254"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group consent-group">
              <label className="consent-label" htmlFor="doctor1Consent">
                <input
                  id="doctor1Consent"
                  name="consent"
                  type="checkbox"
                  checked={doctor1.consent}
                  onChange={updateDoctor1}
                  required
                />
                <span className="consent-text">
                  I agree to register for the NAPCON Quiz.<br />
                  I consent to receive important updates, reminders, and notifications about the NAPCON Quiz via email and WhatsApp through quiz coordinator.
                </span>
              </label>
            </div>

          </div>

          <div className="doctor-section">
            <h3 className="section-title">Participant 2 Details</h3>
            
            <div className="form-group">
              <label>Doctor Name <span className="required">*</span></label>
              <input 
                name="doctorName" 
                value={doctor2.doctorName} 
                onChange={updateDoctor2} 
                required 
              />
            </div>

            <div className="form-group">
              <label>Qualification <span className="required">*</span></label>
                <select
                  name="qualification"
                  value={doctor2.qualification}
                  onChange={updateDoctor2}
                  required
                >
                  <option value="">Select qualification</option>
                  {QUALIFICATIONS.map(qualification => (
                    <option key={qualification} value={qualification}>{qualification}</option>
                  ))}
                </select>
            </div>

            <div className="form-group">
              <label>WhatsApp Number <span className="required">*</span></label>
              <input 
                name="phoneNumber" 
                type="tel"
                value={doctor2.phoneNumber} 
                onChange={updateDoctor2} 
                placeholder="10 digit number"
                maxLength="10"
                inputMode="numeric"
                pattern="[6-9][0-9]{9}"
                title="Enter a valid 10-digit Indian mobile number"
                autoComplete="tel"
                required
              />
            </div>

            <div className="form-group">
              <label>Email <span className="required">*</span></label>
              <input 
                name="email" 
                type="email" 
                value={doctor2.email}
                onChange={updateDoctor2}
                maxLength="254"
                autoComplete="email"
                required
              />
            </div>

            <div className="form-group consent-group">
              <label className="consent-label" htmlFor="doctor2Consent">
                <input
                  id="doctor2Consent"
                  name="consent"
                  type="checkbox"
                  checked={doctor2.consent}
                  onChange={updateDoctor2}
                  required
                />
                <span className="consent-text">
                  I agree to register for the NAPCON Quiz.<br />
                  I consent to receive important updates, reminders, and notifications about the NAPCON Quiz via email and WhatsApp through quiz coordinator.
                </span>
              </label>
            </div>

          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Please wait...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginForm;
